// Frontend crash reporting (Phase 14.4). This audience (55–75, old Android /
// iOS Safari) is exactly where JS breaks, and backend Sentry can't see it.
//
// Sentry is a THIRD PARTY, so it's disclosed in the Privacy page and scoped as
// tightly as the app's privacy posture demands:
//   • no IP / no user identifiers      (sendDefaultPii: false)
//   • no tracing / no session replay   (errors only)
//   • query strings stripped from every URL — the located lookup carries the
//     user's GPS coords as ?lat=&lon=, which must never reach Sentry.
//
// Loaded via dynamic import ONLY when VITE_SENTRY_DSN is set, so the ~40 KB SDK
// never ships in the bundle when disabled (dev, tests, or until configured).

// Drop the query string (keeps the path, e.g. /api/products/<ean>) so GPS
// coordinates in ?lat=&lon= never leave the browser.
function stripQuery(url) {
  if (typeof url !== 'string') return url
  const q = url.indexOf('?')
  return q === -1 ? url : url.slice(0, q)
}

export function initErrorMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) return

  import('@sentry/browser')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        sendDefaultPii: false, // no IP address, no user identifiers
        tracesSampleRate: 0, // errors only — no performance tracing
        // Scrub coords out of fetch/xhr/navigation breadcrumb URLs.
        beforeBreadcrumb(breadcrumb) {
          if (breadcrumb?.data?.url) breadcrumb.data.url = stripQuery(breadcrumb.data.url)
          if (breadcrumb?.data?.to) breadcrumb.data.to = stripQuery(breadcrumb.data.to)
          if (breadcrumb?.data?.from) breadcrumb.data.from = stripQuery(breadcrumb.data.from)
          return breadcrumb
        },
        // Scrub the request URL + drop any headers/cookies/query from the event.
        beforeSend(event) {
          if (event.request) {
            if (event.request.url) event.request.url = stripQuery(event.request.url)
            delete event.request.query_string
            delete event.request.cookies
            delete event.request.headers
          }
          return event
        },
      })
    })
    .catch(() => {
      // SDK failed to load — measurement is best-effort, never break the app.
    })
}
